/*
 * The day/night terminator, for the little map that rides along with each
 * tutorial lesson.
 *
 * The map exists to answer "where am I standing while this sky turns", and half
 * of that answer is which parts of the Earth are in darkness at the instant
 * being shown. Lesson one advances the clock six hours without moving the
 * observer, so the shadow sweeping westward across the map is the only thing
 * that makes the passage of time legible down there; lesson five holds one
 * instant while the observer hops three time zones, and the fixed shadow is
 * what shows Madrid deep in the night while Denver has only just lost the Sun.
 *
 * It is computed from the same solar position the game uses to decide whether a
 * round is dark enough to play, so the map and the sky can never disagree.
 */

import { gmstDegrees, julianDate, normalizeDeg, sunPosition } from '../astronomy.mjs';

const DEG = Math.PI / 180;

/*
 * The point with the Sun straight overhead. Its latitude is the Sun's
 * declination; its longitude is however far the Sun's right ascension has run
 * ahead of the Greenwich meridian.
 */
export function subsolarPoint(date) {
  const jd = julianDate(date);
  const { ra, dec } = sunPosition(jd);
  const lon = normalizeDeg(ra - gmstDegrees(jd));
  return { lat: dec, lon: lon > 180 ? lon - 360 : lon };
}

/*
 * Where the terminator crosses a given meridian.
 *
 * The Sun sits on the horizon where cos of the angular distance to the subsolar
 * point is zero, which reduces to tan(lat) = -cos(H) / tan(dec) for hour angle
 * H. Single-valued in longitude, so the whole curve is one sweep across the map
 * with no branch cuts to handle.
 *
 * Near an equinox the declination approaches zero and the curve stands up
 * vertically along two meridians. That is the true shape rather than a defect,
 * but the tangent has to be nudged off zero or it divides by nothing.
 */
export function terminatorLatitude(lonDeg, subsolar) {
  const hourAngle = (lonDeg - subsolar.lon) * DEG;
  let tanDec = Math.tan(subsolar.lat * DEG);
  if (Math.abs(tanDec) < 1e-9) tanDec = subsolar.lat < 0 ? -1e-9 : 1e-9;
  return Math.atan(-Math.cos(hourAngle) / tanDec) / DEG;
}

/*
 * The shaded region, as a closed lon/lat ring ready to project.
 *
 * Whichever pole the Sun is tilted away from is the one in permanent darkness,
 * so the ring closes along that edge of the map: the night lies south of the
 * curve through northern summer and north of it through southern summer.
 */
export function nightRing(date, stepDeg = 2) {
  const subsolar = subsolarPoint(date);
  const darkPole = subsolar.lat >= 0 ? -90 : 90;

  const ring = [];
  for (let lon = -180; lon <= 180; lon += stepDeg) {
    ring.push([lon, terminatorLatitude(lon, subsolar)]);
  }
  ring.push([180, darkPole], [-180, darkPole]);
  return ring;
}
